import { Injectable } from '@angular/core';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { CapacitorUpdater } from '@capgo/capacitor-updater';
import { ToastController } from '@ionic/angular';
import { BUILD_ID, BUILD_TIME } from '../../build-id';
import { environment } from '../../environments/environment';
import { SoftphoneService } from '../softphone/softphone.service';

// скільки застосунок має пробути у фоні, щоб увімкнути завантажену версію без питань
const APPLY_AFTER_BACKGROUND_MS = 2 * 60 * 1000;
// збірка, що працювала минулого разу: змінилась — показуємо «Застосунок оновлено»
const LAST_BUILD_KEY = 'live_update_last_build';

interface LiveUpdateManifest {
  version: string;
  url: string;
  checksum: string;
  createdAt?: string;
}

/**
 * OTA-оновлення веб-коду нативного застосунку без сторонніх сервісів.
 * Пакет (zip) і latest.json лежать на нашому сервері — їх викладає `yarn deploy-mobile`.
 * Нова версія завантажується у фоні, після чого користувач бачить повідомлення з кнопкою «Оновити».
 * Без натискання вона вмикається сама: при поверненні після 2+ хв у фоні або при наступному запуску.
 * Під час розмови у вбудованому телефоні не вмикаємо — перезапуск сторінки обірвав би дзвінок.
 * Якщо нова версія не стартувала (не викликала notifyAppReady) — плагін сам повертає попередню.
 */
@Injectable({ providedIn: 'root' })
export class LiveUpdateService {
  private isChecking = false;
  private preparedVersion: string | null = null;
  private preparedBundleId: string | null = null;
  private backgroundedAt: number | null = null;
  private toast: HTMLIonToastElement | null = null;
  // натиснули «Оновити» під час розмови — вмикаємо, щойно вона закінчиться
  private applyAfterCall = false;

  constructor(private toastController: ToastController, private softphone: SoftphoneService) {}

  get isAvailable(): boolean {
    return Capacitor.isNativePlatform() && !!environment.liveUpdateUrl;
  }

  // коротка позначка збірки для меню — щоб було видно, яка версія зараз працює
  get buildLabel(): string {
    return BUILD_ID.slice(0, 8);
  }

  async init(): Promise<void> {
    if (!Capacitor.isNativePlatform()) return;

    // Перше, що робимо: підтверджуємо, що поточна версія успішно запустилась
    await CapacitorUpdater.notifyAppReady().catch(err => console.error('[LiveUpdate] notifyAppReady', err));
    this.reportApplied();
    this.softphone.state$.subscribe(state => {
      if (this.applyAfterCall && !state.call) {
        this.applyAfterCall = false;
        this.apply();
      }
    });

    if (!this.isAvailable) return;
    this.check(true);
    App.addListener('appStateChange', ({ isActive }) => {
      if (!isActive) {
        this.backgroundedAt = Date.now();
        return;
      }
      // повернулись після довгої паузи, а нова версія вже завантажена — вмикаємо її одразу
      const wasAwayLong = this.backgroundedAt !== null && Date.now() - this.backgroundedAt >= APPLY_AFTER_BACKGROUND_MS;
      this.backgroundedAt = null;
      if (wasAwayLong && this.preparedBundleId && this.apply()) return;
      this.check();
    });
  }

  // applyDownloadedNow — щойно запуск: пакет, завантажений у минулій сесії, вмикаємо одразу (користувач ще нічого не робить)
  async check(applyDownloadedNow = false): Promise<void> {
    if (!this.isAvailable || this.isChecking) return;
    this.isChecking = true;
    try {
      const response = await fetch(`${environment.liveUpdateUrl}/latest.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!response.ok) return;
      const manifest: LiveUpdateManifest = await response.json();

      // вже працюємо на цій версії
      if (!manifest?.version || manifest.version === BUILD_ID) return;
      // пакет на сервері старіший за цей код (нова нативна збірка ще не викладена через deploy-mobile) —
      // не відкочуємось на нього, інакше нові виправлення зникають після першого ж перезапуску
      if (manifest.createdAt && Date.parse(manifest.createdAt) <= Date.parse(BUILD_TIME)) return;
      // вже підготували — лише нагадуємо, якщо повідомлення закрили
      if (manifest.version === this.preparedVersion) {
        this.offerUpdate();
        return;
      }

      // Плагін каже, що ця версія вже поточна, а BUILD_ID у коді інший (пакет зібрано з неправильним BUILD_ID) —
      // не ставимо її знову, інакше застосунок перезапускається без кінця
      const current = await CapacitorUpdater.current();
      if (current.bundle?.version === manifest.version) {
        console.warn(`[LiveUpdate] Версія ${manifest.version} вже поточна, але BUILD_ID=${BUILD_ID} — пропускаємо`);
        return;
      }

      const existing = (await CapacitorUpdater.list()).bundles
        .find(bundle => bundle.version === manifest.version && bundle.status !== 'error');
      const bundle = existing ?? await CapacitorUpdater.download({
        url: manifest.url,
        version: manifest.version,
        checksum: manifest.checksum
      });
      this.preparedVersion = manifest.version;
      this.preparedBundleId = bundle.id;

      if (applyDownloadedNow && existing && this.apply()) return;

      // Нативно плагін вмикає пакет лише при наступному запуску (після закриття застосунку): автоматичне
      // вмикання у фоні перезапускало сторінку навіть посеред дзвінка — це робимо самі, з перевірками.
      await CapacitorUpdater.next({ id: bundle.id });
      await CapacitorUpdater.setMultiDelay({ delayConditions: [{ kind: 'kill' }] });
      console.log(`[LiveUpdate] Версію ${manifest.version} завантажено`);
      this.offerUpdate();
    } catch (err) {
      console.error('[LiveUpdate] Не вдалося перевірити/завантажити оновлення', err);
    } finally {
      this.isChecking = false;
    }
  }

  // Вмикає завантажену версію (сторінка перезапускається). false — зараз не можна (йде розмова).
  private apply(): boolean {
    if (!this.preparedBundleId || this.softphone.state$.value.call) return false;
    this.toast?.dismiss();
    CapacitorUpdater.set({ id: this.preparedBundleId }).catch(err => console.error('[LiveUpdate] set', err));
    return true;
  }

  private async offerUpdate(): Promise<void> {
    if (this.toast) return;
    this.toast = await this.toastController.create({
      message: 'Завантажено нову версію застосунку',
      position: 'bottom',
      color: 'dark',
      buttons: [
        {
          text: 'Оновити',
          handler: () => {
            if (this.apply()) return true;
            this.applyAfterCall = true;
            this.showMessage('Оновимо після завершення розмови');
            return true;
          }
        },
        { icon: 'close', role: 'cancel' }
      ]
    });
    this.toast.onDidDismiss().then(() => (this.toast = null));
    await this.toast.present();
  }

  private reportApplied(): void {
    const previous = localStorage.getItem(LAST_BUILD_KEY);
    localStorage.setItem(LAST_BUILD_KEY, BUILD_ID);
    if (previous && previous !== BUILD_ID) this.showMessage(`Застосунок оновлено (збірка ${this.buildLabel})`);
  }

  private async showMessage(message: string): Promise<void> {
    const toast = await this.toastController.create({ message, duration: 2500, position: 'bottom', color: 'dark' });
    await toast.present();
  }
}
